import Image from "next/image";
import { ArrowRight } from "lucide-react";
import type { CSSProperties } from "react";

import { CcPhotoScene } from "@/components/services/shared/CcPhotoScene";
import { Mark } from "@/components/services/shared/demo-parts";

import type { AiAutomationDemoCopy } from "./ai-automation-demo-copy";

/**
 * The three "Värdet av AI-automation" tabs as real photos with cards for the
 * made-up property services company laid over them: hours saved, flows that
 * run around the clock, faster replies. Copy comes from `ai-automation-demo.json`
 * (meter rows from _ai-value-photos, bars from _seo-value-photos).
 */

const delay = (d: number) => ({ "--d": d }) as CSSProperties;

// ---------------------------------------------------------------- 1. hours saved

export function AutoTimePhotoView({
  copy,
}: {
  copy: AiAutomationDemoCopy["time"];
}) {
  const max = Math.max(...copy.savedSeries);
  return (
    <CcPhotoScene photo={copy.photo} variant="auto-time">
      <div className="cc-wv-card cc-auv-saved" style={delay(0)}>
        <p className="cc-wv-card__head">
          {copy.savedTitle}
          <em>{copy.savedMeta}</em>
        </p>
        <p className="cc-auv-kpi">
          <b>{copy.savedValue}</b>
          <span>{copy.savedPeriod}</span>
        </p>
        <ul className="cc-sv-visits__bars">
          {copy.savedSeries.map((value, index) => (
            <li
              key={index}
              style={
                {
                  "--h": `${(value / max) * 100}%`,
                  "--i": index,
                } as CSSProperties
              }
            />
          ))}
        </ul>
        <small className="cc-auv-kpi__change">{copy.savedChange}</small>
      </div>

      <div className="cc-wv-card cc-auv-flows" style={delay(1)}>
        <p className="cc-wv-card__head">{copy.flowsTitle}</p>
        <ul className="cc-av-meters">
          {copy.flows.map((flow, index) => (
            <li key={flow.label} style={{ "--i": index } as CSSProperties}>
              <Mark logo={flow.logo} badge={flow.badge} />
              <span>{flow.label}</span>
              <i
                className="cc-av-meters__bar"
                style={{ "--w": `${flow.share * 100}%` } as CSSProperties}
              />
              <b>{flow.valueText}</b>
            </li>
          ))}
        </ul>
      </div>
    </CcPhotoScene>
  );
}

// ---------------------------------------------------------------- 2. flows that run

export function AutoRunsPhotoView({
  copy,
}: {
  copy: AiAutomationDemoCopy["runs"];
}) {
  return (
    <CcPhotoScene photo={copy.photo} variant="auto-runs">
      <div className="cc-wv-card cc-auv-runs" style={delay(0)}>
        <p className="cc-wv-card__head">
          {copy.runsTitle}
          <em>{copy.runsMeta}</em>
        </p>
        <p className="cc-auv-kpi">
          <b>{copy.runsValue}</b>
          <span>{copy.runsPeriod}</span>
        </p>
        <table className="cc-auv-table">
          <thead>
            <tr>
              <th scope="col">{copy.columns.flow}</th>
              <th scope="col">{copy.columns.runs}</th>
              <th scope="col">{copy.columns.ok}</th>
            </tr>
          </thead>
          <tbody>
            {copy.table.map((row) => (
              <tr key={row.label}>
                <th scope="row">
                  <Mark logo={row.logo} badge={row.badge} />
                  {row.label}
                </th>
                <td>{row.runs}</td>
                <td>
                  <b>{row.ok}</b>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="cc-wv-card cc-auv-events" style={delay(1)}>
        <p className="cc-wv-card__head">{copy.eventsTitle}</p>
        <ul>
          {copy.events.map((event) => (
            <li key={event.name}>
              <span>{event.name}</span>
              <em data-tone={event.tone}>{event.status}</em>
            </li>
          ))}
        </ul>
      </div>
    </CcPhotoScene>
  );
}

// ---------------------------------------------------------------- 3. faster replies

export function AutoRepliesPhotoView({
  copy,
}: {
  copy: AiAutomationDemoCopy["replies"];
}) {
  return (
    <CcPhotoScene photo={copy.photo} variant="auto-replies">
      <div className="cc-wv-card cc-auv-inbox" style={delay(0)}>
        <p className="cc-wv-card__head">
          {copy.inboxTitle}
          <em>{copy.inboxMeta}</em>
        </p>
        <ul>
          {copy.rows.slice(0, 3).map((row) => (
            <li key={row.name}>
              {row.photo ? (
                <Image src={row.photo} alt="" width={30} height={30} />
              ) : (
                <i>{row.initials}</i>
              )}
              <span>
                {row.name}
                <small>{row.subject}</small>
              </span>
              <em data-tone={row.tone}>{row.status}</em>
            </li>
          ))}
        </ul>
      </div>

      <div className="cc-wv-card cc-auv-rate" style={delay(1)}>
        <p className="cc-wv-card__head">{copy.rateTitle}</p>
        <div className="cc-auv-rate__row">
          <span>
            <small>{copy.rateBeforeLabel}</small>
            <b>{copy.rateBefore}</b>
          </span>
          <ArrowRight size={16} strokeWidth={2} />
          <span data-strong="true">
            <small>{copy.rateAfterLabel}</small>
            <b>{copy.rateAfter}</b>
          </span>
        </div>
      </div>
    </CcPhotoScene>
  );
}
